import { useMemo, useState } from 'react'
import type { CSSProperties, ReactElement } from 'react'
import { Link } from 'react-router'
import { useT } from '../i18n/useI18n'
import ServiceNotice from '../components/ServiceNotice'
import PriceStatusBadge from '../components/PriceStatusBadge'
import { legalPages } from '../data/legal/legalPages'
import {
  pricingGroups,
  analysisApps,
  RESOURCE_WEIGHT,
  computeMix,
  groupUpsells,
  monthlyEquivalent,
  formatNok,
  ALL_APPS_PRICE_NOK,
  HAS_INDICATIVE_PRICES,
  HAS_BETA_PRICES,
  type Interval,
  type PlanMode,
  type PricingApp,
  type PricingGroup,
  type ResourceClass,
} from '../data/pricing'
import './products.css'

const WEIGHT_BARS = [0, 1, 2]
const DEDICATED_FEATURE_KEYS: readonly string[] = ['f1', 'f2', 'f3', 'f4']

function WeightMeter({ resourceClass, t }: { resourceClass: ResourceClass; t: (k: string, vars?: Record<string, string | number>) => string }): ReactElement {
  const filled = RESOURCE_WEIGHT[resourceClass]
  return (
    <span className={`pm-weight pm-weight--${resourceClass}`} title={t('pricing.weight.hint')} aria-label={t(`pricing.weight.${resourceClass}`)}>
      <span className="pm-gauge" aria-hidden="true">
        {WEIGHT_BARS.map((i) => <span key={i} className={`pm-bar ${i < filled ? 'pm-bar--on' : ''}`} />)}
      </span>
      <span className="pm-weight-label">{t(`pricing.weight.${resourceClass}`)}</span>
    </span>
  )
}

function Price({ value, per }: { value: number; per: string }): ReactElement {
  return (
    <span className="pm-price">
      <span className="pm-price-cur">kr</span>
      <span className="pm-price-num">{formatNok(value)}</span>
      <span className="pm-price-per">{per}</span>
    </span>
  )
}

function StandaloneCard({ app, index, selected, onToggle, interval, t }: {
  app: PricingApp; index: number; selected: boolean; onToggle: () => void; interval: Interval; t: (k: string, vars?: Record<string, string | number>) => string
}): ReactElement {
  return (
    <button
      type="button"
      className={`pm-card pm-card--${app.resourceClass} ${selected ? 'pm-card--selected' : ''}`}
      aria-pressed={selected}
      onClick={onToggle}
      style={{ '--pm-delay': `${index * 45}ms` } as CSSProperties}
    >
      <span className="pm-node" aria-hidden="true" />
      <span className="pm-card-top">
        <span className="pm-card-icon"><i className={`bi bi-${app.icon}`} /></span>
        <WeightMeter resourceClass={app.resourceClass} t={t} />
      </span>
      <span className="pm-card-name">
        {t(`${app.i18nKey}.name`)}
        {app.methodTag && <span className="pm-method">{app.methodTag}</span>}
      </span>
      {app.launch?.kind === 'download' && <span className="pm-platform"><i className="bi bi-windows" /> {t('pricing.desktopBadge')}</span>}
      <span className="pm-card-pitch">{t(`${app.i18nKey}.pitch`)}</span>
      <span className="pm-card-foot">
        <Price value={monthlyEquivalent(app.priceNok, interval)} per={t('pricing.perMonth')} />
        {app.priceStatus && <PriceStatusBadge status={app.priceStatus} />}
      </span>
    </button>
  )
}

function GroupCard({ group, groupSelected, selectedIds, onToggleGroup, onToggleMember, interval, t }: {
  group: PricingGroup; groupSelected: boolean; selectedIds: ReadonlySet<string>
  onToggleGroup: () => void; onToggleMember: (id: string) => void; interval: Interval; t: (k: string, vars?: Record<string, string | number>) => string
}): ReactElement {
  return (
    <div className={`pm-group ${groupSelected ? 'pm-group--selected' : ''}`}>
      <div className="pm-group-head">
        <span className="pm-group-icon"><i className={`bi bi-${group.icon}`} /></span>
        <div className="pm-group-headtext">
          <div className="pm-group-name">
            {t(`${group.i18nKey}.name`)}
            <span className="pm-group-count">{t('pricing.group.toolsMany', { n: group.members.length })}</span>
          </div>
          <div className="pm-group-pitch">{t(`${group.i18nKey}.pitch`)}</div>
        </div>
      </div>

      {group.groupPriceNok === null ? (
        <div className="pm-group-whole pm-group-whole--forming">
          <span className="pm-forming-badge">{t('pricing.group.forming')}</span>
          <span className="pm-group-whole-label">{t('pricing.group.formingNote')}</span>
        </div>
      ) : (
        <button
          type="button"
          className={`pm-group-whole ${groupSelected ? 'is-on' : ''}`}
          aria-pressed={groupSelected}
          onClick={onToggleGroup}
        >
          <span className="pm-node" aria-hidden="true" />
          <span className="pm-group-whole-label">{t('pricing.group.whole')}</span>
          <span className="pm-group-whole-price">
            <Price value={monthlyEquivalent(group.groupPriceNok, interval)} per={t('pricing.perMonth')} />
            {group.priceStatus && <PriceStatusBadge status={group.priceStatus} />}
          </span>
        </button>
      )}

      <ul className="pm-members">
        {group.members.map((m) => {
          const on = groupSelected || selectedIds.has(m.id)
          return (
            <li key={m.id}>
              <button
                type="button"
                className={`pm-member pm-member--${m.resourceClass} ${on ? 'pm-member--on' : ''} ${groupSelected ? 'pm-member--implied' : ''}`}
                aria-pressed={on}
                disabled={groupSelected}
                onClick={() => onToggleMember(m.id)}
              >
                <span className="pm-node pm-node--sm" aria-hidden="true" />
                <span className="pm-member-icon"><i className={`bi bi-${m.icon}`} /></span>
                <span className="pm-member-text">
                  <span className="pm-member-name">{t(`${m.i18nKey}.name`)}</span>
                  <span className="pm-member-pitch">{t(`${m.i18nKey}.pitch`)}</span>
                </span>
                <span className="pm-member-right">
                  {groupSelected
                    ? <span className="pm-included">{t('pricing.group.included')}</span>
                    : <Price value={monthlyEquivalent(m.priceNok, interval)} per={t('pricing.perMonth')} />}
                  <WeightMeter resourceClass={m.resourceClass} t={t} />
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default function Products(): ReactElement {
  const t = useT()
  const [mode, setMode] = useState<PlanMode>('mix')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [interval, setInterval] = useState<Interval>('monthly')
  const [licenses, setLicenses] = useState(1)

  const toggleId = (id: string, clearMembersOf?: PricingGroup): void => {
    setMode('mix')
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else {
        next.add(id)
        // Selecting a whole group supersedes its individual members.
        if (clearMembersOf) for (const m of clearMembersOf.members) next.delete(m.id)
      }
      return next
    })
  }

  const mix = useMemo(() => computeMix(selected), [selected])
  const upsells = useMemo(() => groupUpsells(selected), [selected])

  const perMonthEach = mode === 'all'
    ? monthlyEquivalent(ALL_APPS_PRICE_NOK, interval)
    : monthlyEquivalent(mix.discountedMonthly, interval)
  const monthlyTotal = Math.round(perMonthEach * licenses)
  const annualTotal = monthlyTotal * 12
  const nextTier = mix.count === 2 ? 15 : mix.count === 3 ? 20 : 0

  let standaloneIndex = 0

  return (
    <section className="section pm-page">
      <div className="pm-grid-bg" aria-hidden="true" />
      <div className="container pm-container">
        <header className="pm-hero">
          <div className="pm-eyebrow">{t('pricing.eyebrow')}</div>
          <h1 className="pm-hero-title">{t('pricing.title')}</h1>
          <p className="pm-hero-lead">{t('pricing.subtitle')}</p>
          <div className="pm-interval" role="group" aria-label={t('pricing.interval.label')}>
            <button type="button" className={`pm-interval-btn ${interval === 'monthly' ? 'is-active' : ''}`} onClick={() => setInterval('monthly')} aria-pressed={interval === 'monthly'}>
              {t('pricing.interval.monthly')}
            </button>
            <button type="button" className={`pm-interval-btn ${interval === 'annual' ? 'is-active' : ''}`} onClick={() => setInterval('annual')} aria-pressed={interval === 'annual'}>
              {t('pricing.interval.annual')}<span className="pm-interval-save">{t('pricing.interval.save')}</span>
            </button>
          </div>
        </header>

        <div className="pm-layout">
          <div className="pm-builder">
            <div className="pm-steplabel">{t('pricing.step.modules')}</div>

            {pricingGroups.map((g) => (
              <GroupCard
                key={g.id}
                group={g}
                groupSelected={mode === 'mix' && selected.has(g.id)}
                selectedIds={mode === 'mix' ? selected : new Set()}
                onToggleGroup={() => toggleId(g.id, g)}
                onToggleMember={(id) => toggleId(id)}
                interval={interval}
                t={t}
              />
            ))}

            <div className="pm-steplabel">{t('pricing.step.analysis')}</div>
            <div className="pm-grid">
              {analysisApps.map((a) => (
                <StandaloneCard key={a.id} app={a} index={standaloneIndex++} selected={mode === 'mix' && selected.has(a.id)} onToggle={() => toggleId(a.id)} interval={interval} t={t} />
              ))}
            </div>

            <div className="pm-steplabel">{t('pricing.step.bundles')}</div>
            <button type="button" className={`pm-all ${mode === 'all' ? 'pm-all--selected' : ''}`} aria-pressed={mode === 'all'} onClick={() => setMode('all')}>
              <span className="pm-all-ribbon">{t('pricing.allApps.badge')}</span>
              <span className="pm-all-body">
                <span className="pm-all-head">
                  <span className="pm-all-icon"><i className="bi bi-grid-3x3-gap-fill" /></span>
                  <span>
                    <span className="pm-all-name">{t('pricing.allApps.title')}</span>
                    <span className="pm-all-tagline">{t('pricing.allApps.tagline')}</span>
                  </span>
                </span>
                <Price value={monthlyEquivalent(ALL_APPS_PRICE_NOK, interval)} per={t('pricing.perMonth')} />
              </span>
              <span className="pm-all-note"><i className="bi bi-arrow-return-right" /> {t('pricing.allApps.note')}</span>
            </button>

            <ServiceNotice className="pm-service" />

            <div className={`pm-dedicated ${mode === 'dedicated' ? 'pm-dedicated--selected' : ''}`}>
              <div className="pm-ded-grid" aria-hidden="true" />
              <div className="pm-ded-head">
                <span className="pm-ded-icon"><i className="bi bi-hdd-rack" /></span>
                <div>
                  <div className="pm-ded-name">{t('pricing.dedicated.title')}</div>
                  <div className="pm-ded-tagline">{t('pricing.dedicated.tagline')}</div>
                </div>
              </div>
              <ul className="pm-ded-list">
                {DEDICATED_FEATURE_KEYS.map((k) => <li key={k}><i className="bi bi-check2" />{t(`pricing.dedicated.${k}`)}</li>)}
              </ul>
              <Link to="/contact" className="pm-ded-cta" onClick={() => setMode('dedicated')}>
                {t('pricing.dedicated.cta')} <i className="bi bi-arrow-right" />
              </Link>
            </div>
          </div>

          <aside className="pm-summary">
            <div className="pm-summary-head">
              <h2 className="pm-summary-title">{t('pricing.summary.title')}</h2>
              <span className="pm-summary-tag">{t('pricing.summary.tag')}</span>
            </div>

            {mode === 'dedicated' && (
              <div className="pm-summary-body">
                <p className="pm-summary-lead">{t('pricing.dedicated.summary')}</p>
                <Link to="/contact" className="pm-cta-btn">{t('pricing.dedicated.cta')}</Link>
              </div>
            )}

            {mode === 'all' && (
              <div className="pm-summary-body">
                <ul className="pm-lines">
                  <li className="pm-line"><span className="pm-line-name">{t('pricing.allApps.title')}</span><span className="pm-leader" /><span className="pm-line-val">kr {formatNok(monthlyEquivalent(ALL_APPS_PRICE_NOK, interval))}</span></li>
                </ul>
                {renderLicenses()}{renderTotal()}{renderCta()}
              </div>
            )}

            {mode === 'mix' && (
              <div className="pm-summary-body">
                {mix.count === 0 && (
                  <div className="pm-empty">
                    <span className="pm-empty-mark"><i className="bi bi-plus-lg" /></span>
                    <p>{t('pricing.summary.empty')}</p>
                  </div>
                )}
                {mix.count > 0 && (
                  <>
                    <ul className="pm-lines">
                      {mix.items.map((it) => (
                        <li key={it.id} className="pm-line">
                          <span className="pm-line-name">{t(it.nameKey)}{it.isGroup && <span className="pm-line-badge">{t('pricing.group.whole')}</span>}</span>
                          <span className="pm-leader" />
                          <span className="pm-line-val">kr {formatNok(it.priceNok)}</span>
                        </li>
                      ))}
                    </ul>
                    {mix.discountPct > 0 && (
                      <div className="pm-subtotals">
                        <div className="pm-line pm-line--muted"><span className="pm-line-name">{t('pricing.summary.subtotal')}</span><span className="pm-leader" /><span className="pm-line-val">kr {formatNok(mix.subtotalMonthly)}</span></div>
                        <div className="pm-line pm-line--save"><span className="pm-line-name">{t('pricing.summary.discount', { pct: Math.round(mix.discountPct * 100) })}</span><span className="pm-leader" /><span className="pm-line-val">−kr {formatNok(mix.subtotalMonthly - mix.discountedMonthly)}</span></div>
                      </div>
                    )}
                    {renderLicenses()}{renderTotal()}

                    {upsells.map((u) => (
                      <button key={u.groupId} type="button" className="pm-nudge" onClick={() => toggleId(u.groupId, pricingGroups.find((g) => g.id === u.groupId))}>
                        <i className="bi bi-box-seam" /> {t('pricing.nudge.group', { name: t(u.nameKey), price: formatNok(monthlyEquivalent(u.groupPriceNok, interval)) })}
                      </button>
                    ))}
                    {mix.allAppsCheaper && (
                      <button type="button" className="pm-nudge" onClick={() => setMode('all')}>
                        <i className="bi bi-stars" /> {t('pricing.nudge.allApps', { price: formatNok(monthlyEquivalent(ALL_APPS_PRICE_NOK, interval)) })}
                      </button>
                    )}
                    {!mix.allAppsCheaper && upsells.length === 0 && nextTier > 0 && (
                      <p className="pm-nudge-soft"><i className="bi bi-plus-circle" /> {t('pricing.nudge.addMore', { pct: nextTier })}</p>
                    )}
                    {renderCta()}
                  </>
                )}
              </div>
            )}

            <p className="pm-footnote">{t('pricing.footnote')}{HAS_INDICATIVE_PRICES && ` ${t('pricing.priceStatus.indicative.hint')}`}{HAS_BETA_PRICES && ` ${t('pricing.priceStatus.beta.hint')}`}</p>
          </aside>
        </div>
      </div>
    </section>
  )

  function renderLicenses(): ReactElement {
    return (
      <div className="pm-licenses">
        <span className="pm-licenses-label">{t('pricing.licenses')}</span>
        <span className="pm-stepper">
          <button type="button" aria-label={t('pricing.licensesDown')} onClick={() => setLicenses((n) => Math.max(1, n - 1))} disabled={licenses <= 1}>−</button>
          <span className="pm-stepper-val">{licenses}</span>
          <button type="button" aria-label={t('pricing.licensesUp')} onClick={() => setLicenses((n) => Math.min(99, n + 1))}>+</button>
        </span>
      </div>
    )
  }

  function renderTotal(): ReactElement {
    return (
      <div className="pm-total">
        <span className="pm-total-label">{t('pricing.summary.total')}</span>
        <span className="pm-total-val"><span className="pm-total-cur">kr</span> {formatNok(monthlyTotal)}<span className="pm-total-per"> {t('pricing.perMonth')}</span></span>
        {interval === 'annual' && <span className="pm-total-annual">{t('pricing.billedAnnually', { total: formatNok(annualTotal) })}</span>}
      </div>
    )
  }

  function renderCta(): ReactElement {
    return (
      <div className="pm-cta">
        <button type="button" className="pm-cta-btn" disabled>{t('pricing.cta.continue')}</button>
        <p className="pm-cta-note">{t('pricing.cta.note')} <Link to="/contact">{t('pricing.cta.contact')}</Link></p>
        <p className="pm-cta-note">{t('pricing.cta.termsLead')} <Link to={legalPages.terms.path}>{t('pricing.cta.termsLink')}</Link>.</p>
      </div>
    )
  }
}
